import { getPostalVerificationCopy } from './postalVerificationCopy'

const activeSubscriptionStatuses = new Set(['active', 'trialing'])
const badgeColors = {
  starter: '#B87333',
  basic: '#A7A9AC',
  premium: '#f39200',
}

function normalizePlan(plan) {
  const normalized = String(plan || '').toLowerCase()
  if (normalized === 'premium' || normalized === 'basic' || normalized === 'starter') return normalized
  return 'starter'
}

export function getCompanyPlan(company, fallbackPlan = 'starter') {
  const subscription = Array.isArray(company?.subscriptions)
    ? company.subscriptions[0]
    : company?.subscriptions
  const plan = subscription?.plan?.toLowerCase()
  const status = subscription?.status?.toLowerCase()
  if (plan && (!status || activeSubscriptionStatuses.has(status))) return normalizePlan(plan)
  return normalizePlan(fallbackPlan)
}

export function isDemoCompany(company) {
  return company?.is_demo === true || company?.is_demo === 'true'
}

export function isVerifiedCompany(company) {
  if (!company || isDemoCompany(company) || company?.is_suspended === true) return false

  const status = company.zefix_verification_status
  if (!status) return true

  return ['verified', 'manual_approved', 'postal_verified'].includes(String(status).toLowerCase())
}

export function getCompanyBadgeVariant(company, fallbackPlan = 'starter') {
  if (!isVerifiedCompany(company)) return null
  const plan=getCompanyPlan(company, fallbackPlan)
  // UI variants carry the verification method; entitlements and all sorting
  // continue to use getCompanyPlan's unchanged starter/basic/premium contract.
  return company.zefix_verification_status === 'postal_verified' ? `postal:${plan}` : plan
}

export function isPremiumCompany(company) {
  return isVerifiedCompany(company) && getCompanyPlan(company) === 'premium'
}

export async function attachCompanySubscriptions(supabase, companies) {
  const list = Array.isArray(companies) ? companies : [companies].filter(Boolean)
  const companyIds = [...new Set(list.map(company => company?.id).filter(Boolean))]
  const userIds = [...new Set(list.map(company => company?.user_id).filter(Boolean))]
  if (list.length === 0) return companies

  if (companyIds.length > 0) {
    // RPC responses share PostgREST's row limit. A growing directory must not
    // silently turn companies beyond that limit into Starter accounts.
    const data = []
    let error = null
    for (let offset = 0; offset < companyIds.length; offset += 500) {
      const result = await supabase.rpc('hubbing_company_badges', {
        p_company_ids: companyIds.slice(offset, offset + 500),
      })
      if (result.error) { error = result.error; break }
      data.push(...(result.data || []))
    }

    if (!error) {
      const badgesByCompany = new Map((data || []).map(badge => [badge.company_id, badge]))
      const enriched = list.map(company => {
        const badge = badgesByCompany.get(company.id)
        return {
          ...company,
          subscriptions: badge?.plan
            ? [{ plan: badge.plan, status: badge.status || 'active' }]
            : [],
        }
      })
      return Array.isArray(companies) ? enriched : enriched[0]
    }

    console.warn('Unable to load company badge plans:', error.message)
  }

  if (userIds.length === 0) return companies

  const data = []
  // Keep the fallback GET URL bounded as well (each user id is a UUID).
  for (let offset = 0; offset < userIds.length; offset += 100) {
    const result = await supabase
      .from('subscriptions')
      .select('user_id, plan, status')
      .in('user_id', userIds.slice(offset, offset + 100))
    if (result.error) {
      console.warn('Unable to load company subscriptions:', result.error.message)
      return companies
    }
    data.push(...(result.data || []))
  }

  const subscriptionsByUser = new Map((data || []).map(subscription => [subscription.user_id, subscription]))
  const enriched = list.map(company => ({
    ...company,
    subscriptions: subscriptionsByUser.has(company.user_id)
      ? [subscriptionsByUser.get(company.user_id)]
      : [],
  }))

  return Array.isArray(companies) ? enriched : enriched[0]
}

export function isVerifiedBadgeFeature(feature) {
  const text = String(feature || '').toLowerCase()
  return /badge|verified|verifiziert|verificato|verifie|verifie|vérifié/.test(text)
}

export function VerifiedBadge({ size = 18, variant = 'premium', style = {}, lang = 'fr' }) {
  const postal=String(variant).startsWith('postal:')
  const plan=postal?String(variant).slice(7):variant
  const fill = badgeColors[plan] || badgeColors.premium
  const title=postal?getPostalVerificationCopy(lang).verified:null

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 85.04 85.04"
      aria-hidden={postal ? undefined : true}
      role={postal ? 'img' : undefined}
      aria-label={title || undefined}
      data-verification-method={postal ? 'postal' : undefined}
      style={{ display: 'inline-block', flexShrink: 0, verticalAlign: '-0.18em', ...style }}
    >
      {title && <title>{title}</title>}
      <path
        fill={fill}
        d="M31.94,2.17c.63-.1,1.01,0,1.56.26,2.01.96,4.89,3.85,6.94,5.22,1.48.99,2.02,1.36,3.68.35,2.32-1.42,5.12-4.45,7.39-5.57,1.22-.6,2.61-.21,3.31.96,1.45,2.42,2.25,6.11,3.66,8.63.45.81,1.19,1.47,2.19,1.47,3.01,0,6.64-1.12,9.73-1.2,1.25.09,2.35,1.21,2.36,2.47.03,2.88-1.18,6.55-1.22,9.46-.01,1,.64,1.86,1.46,2.32,2.6,1.44,6.03,2.33,8.72,3.71,1.06.75,1.53,2.08.87,3.27-1.6,2.54-3.98,4.9-5.54,7.42-.82,1.32-.82,1.85,0,3.17,1.57,2.53,3.93,4.88,5.55,7.42.63,1.18.17,2.64-.96,3.32-2.42,1.45-6.09,2.24-8.63,3.66-.89.49-1.47,1.24-1.46,2.32.03,2.88,1.25,6.63,1.22,9.46-.02,1.38-1.16,2.39-2.5,2.5-2.98-.14-6.56-1.19-9.46-1.22-1.08-.01-1.82.57-2.32,1.46-1.41,2.54-2.21,6.23-3.66,8.63-.72,1.19-2.07,1.57-3.31.96-2.24-1.1-5.11-4.13-7.39-5.57-1.21-.76-2.01-.76-3.22,0-2.26,1.42-5.16,4.5-7.39,5.57-1.24.59-2.37.25-3.21-.8-1.46-2.68-2.27-6.21-3.76-8.8-.51-.89-1.22-1.47-2.32-1.46-2.92.03-6.47,1.09-9.46,1.22-1.33-.12-2.36-1.02-2.45-2.39.06-3.02,1.12-6.49,1.17-9.44.02-1.16-.49-1.92-1.46-2.45-2.62-1.45-6.03-2.3-8.72-3.71-1.07-.77-1.49-2.05-.88-3.27,1.15-2.29,4.1-5.07,5.57-7.39.63-1,.86-1.69.27-2.81-1.35-2.54-4.35-5.22-5.84-7.8-.62-1.24-.2-2.6.99-3.29,2.5-1.46,6.01-2.22,8.54-3.62.99-.55,1.46-1.23,1.54-2.39l-1.21-9.33c.05-1.48,1.02-2.49,2.5-2.61,2.99.13,6.52,1.18,9.45,1.21,1.09.01,1.9-.62,2.38-1.53,1.34-2.52,2.18-6.17,3.62-8.54.38-.62.98-1.13,1.72-1.25Z"
      />
      <path
        fill="#fff"
        d="M31.01,37.88l1.57-.78c8.41-3.71,18.16,2.69,18.63,11.71.11,2.05-.44,4.13,1.41,5.6,2.51,2,6,.27,6.23-2.87.16-2.23-.2-4.7,0-6.91.35-4.11,6.07-4.48,6.99-.5-.06,5.7,1.07,11.34-3.69,15.52-6.69,5.86-17,1.62-17.85-7.16-.25-2.52.23-4.43-1.41-6.65-3.11-4.2-9.44-3.2-11.37,1.59-1.17,2.92-.19,8.52-.5,11.85-.09.96-.82,2.05-1.64,2.55-2.26,1.37-5.17-.11-5.38-2.75V22.34c.48-3.29,4.76-4.29,6.54-1.41.13.2.48.9.48,1.09v15.85Z"
      />
    </svg>
  )
}

